import type { TreeOp } from '@/components/tree-dnd/tree-dnd-diff';
import type {
  NodePath,
  TreeGroupNode,
  TreeNode,
} from '@/components/tree-dnd/tree-dnd-types';
import { isGroupNode } from '@/components/tree-dnd/tree-dnd-types';
import {
  getNodeAtPath,
  insertChild,
  moveNode,
  removeNodeAtPath,
  setNegatedAtPath,
  updateConditionAtPath,
  updateGroupOperatorAtPath,
} from '@/components/tree-dnd/tree-dnd-utils';

export function findPathById(root: TreeGroupNode, id: string): NodePath | null {
  if (root.id === id) return [];

  const walk = (node: TreeGroupNode, path: NodePath): NodePath | null => {
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i];
      const childPath = [...path, i];
      if (child.id === id) return childPath;
      if (isGroupNode(child)) {
        const found = walk(child, childPath);
        if (found) return found;
      }
    }
    return null;
  };

  return walk(root, []);
}

function replaceNodeAtPath(
  root: TreeGroupNode,
  path: NodePath,
  next: TreeNode
): TreeGroupNode {
  if (path.length === 0) {
    return isGroupNode(next) ? next : root;
  }

  const without = removeNodeAtPath(root, path);
  if (!without) return root;
  const parentPath = path.slice(0, -1);
  const index = path[path.length - 1];
  return insertChild(without, parentPath, next, index);
}

export function applyTreeOp(root: TreeGroupNode, op: TreeOp): TreeGroupNode {
  switch (op.type) {
    case 'remove': {
      const path = findPathById(root, op.id);
      if (!path || path.length === 0) return root;
      return removeNodeAtPath(root, path) ?? root;
    }
    case 'add': {
      const parentPath = findPathById(root, op.parentId);
      if (!parentPath) return root;
      const parent = getNodeAtPath(root, parentPath);
      if (!parent || !isGroupNode(parent)) return root;
      const index = Math.max(0, Math.min(op.index, parent.children.length));
      return insertChild(root, parentPath, op.node, index);
    }
    case 'move': {
      const sourcePath = findPathById(root, op.id);
      const parentPath = findPathById(root, op.parentId);
      if (!sourcePath || sourcePath.length === 0 || !parentPath) return root;
      return moveNode(root, sourcePath, parentPath, op.index) ?? root;
    }
    case 'update': {
      const path = findPathById(root, op.id);
      if (!path) return root;
      let next = root;
      if (op.patch.negated !== undefined) {
        next = setNegatedAtPath(next, path, op.patch.negated);
      }
      if (op.patch.operator !== undefined) {
        next = updateGroupOperatorAtPath(next, path, op.patch.operator);
      }
      if (op.patch.args !== undefined || op.patch.kind !== undefined) {
        const current = getNodeAtPath(next, path);
        if (current?.type === 'condition') {
          if (op.patch.kind !== undefined && op.patch.kind !== current.kind) {
            // Kind change: replace the whole condition node.
            next = replaceNodeAtPath(next, path, {
              ...current,
              kind: op.patch.kind,
              args: (op.patch.args ?? current.args) as never,
            } as TreeNode);
          } else if (op.patch.args !== undefined) {
            next = updateConditionAtPath(next, path, {
              args: op.patch.args as never,
            });
          }
        }
      }
      return next;
    }
    default:
      return root;
  }
}
